"use client";

import { useRef, useState, useEffect, useCallback, useMemo, memo } from "react";
import { createPortal } from "react-dom";
import { usePathname, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import { ChevronDown } from "lucide-react";
import { Link } from "@/components/atoms/link";
import { generateLinkFromGeneralLink, generateTargetFromLink, LinkConfig } from "@/lib/utils";
import {
  ContentJapanHotelTab,
  Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab,
  Maybe,
} from "@/types/generated";

// Mapping from use_existing_tab enum value to URL prefix
export { EXISTING_TAB_URL_MAP } from "./navLinkUtils";
import { EXISTING_TAB_URL_MAP } from "./navLinkUtils";

export interface ResolvedTab {
  title: string;
  href: string;
  isActive: boolean;
  target: "_blank" | "_self" | "_parent" | "_top";
  subtabs: {
    title: string;
    href: string;
    isActive: boolean;
    target: "_blank" | "_self" | "_parent" | "_top";
  }[];
}

interface CustomTabsNavProps {
  customTabsData: ContentJapanHotelTab[];
  hotelSlug: string;
  withTopPadding?: boolean;
  navRef?: React.RefObject<HTMLDivElement>;
  activeTabRef?: React.MutableRefObject<HTMLAnchorElement | null>;
  navClassName?: string;
  innerClassName?: string;
  isAttractionTemplate?: boolean;
  /** Controls where the active indicator is drawn. Defaults to "bottom" (existing behaviour). */
  activeIndicator?: "top" | "bottom";
}


interface DropdownPortalProps {
  anchorRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  children: React.ReactNode;
}


export const isValidTab = (tabItem: {
  label?: Maybe<string>;
  use_existing_tab?: Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab | null;
  use_custom_link?: boolean | null;
  custom_link?: unknown;
} | null | undefined): boolean => {
  if (!tabItem) return false;
  if (!tabItem.label || tabItem.label.trim() === "") return false;
  if (tabItem.use_existing_tab) return true;
  if (tabItem.use_custom_link && tabItem.custom_link) return true;
  return false;
};

const tabLinkClass = (isActive: boolean, withTopPadding: boolean, activeIndicator: "top" | "bottom" = "bottom") => {
  if (activeIndicator === "top") {
    return [
      "relative font-[700] flex h-full cursor-pointer items-center px-4 text-sm tracking-wide whitespace-nowrap transition-colors text-[#1F1F1F]",
      "after:absolute after:top-0 after:left-1/2 after:-translate-x-1/2 after:h-1.75 after:w-7.5 after:content-['']",
      isActive
        ? "after:bg-[#1F1F1F]"
        : "after:bg-transparent hover:after:bg-[#1F1F1F]/30",
    ]
      .filter(Boolean)
      .join(" ");
  }
  return [
    "xl:text-md text-md mx-4 flex cursor-pointer items-center px-1 leading-6 whitespace-nowrap",
    "h-full max-md:h-[calc(var(--mobile-sub-header-height)-2px)] max-md:text-sm",
    withTopPadding ? "pt-2" : "",
    isActive
      ? "border-b-2 !border-[var(--color-primary-950)] font-[var(--font-bold)]! font-[700]! text-[var(--color-primary-950)]"
      : "border-b-2 border-transparent text-[var(--text-base-primary)] hover:border-b-2 hover:border-[var(--color-primary-950)] hover:text-[var(--color-primary-950)]",
  ]
    .filter(Boolean)
    .join(" ");
};

const DROPDOWN_MIN_WIDTH = 160;
const DROPDOWN_MARGIN = 8; // px from screen edge

const DropdownPortal = ({ anchorRef, isOpen, children }: DropdownPortalProps) => {
  const [coords, setCoords] = useState<{ top: number; left: number; transformOrigin: string } | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      const centeredLeft = rect.left + rect.width / 2 + window.scrollX;
      const halfWidth = DROPDOWN_MIN_WIDTH / 2;

      // Clamp so dropdown stays within viewport with margin
      const minLeft = DROPDOWN_MARGIN + halfWidth + window.scrollX;
      const maxLeft = viewportWidth - DROPDOWN_MARGIN - halfWidth + window.scrollX;
      const clampedLeft = Math.min(Math.max(centeredLeft, minLeft), maxLeft);

      // Shift the transform origin so the arrow/caret visually aligns with the tab
      const offsetFromCenter = centeredLeft - clampedLeft;
      const transformOriginX = 50 + (offsetFromCenter / DROPDOWN_MIN_WIDTH) * 100;
      const transformOriginXClamped = Math.min(Math.max(transformOriginX, 10), 90);

      setCoords({
        top: rect.bottom + window.scrollY,
        left: clampedLeft,
        transformOrigin: `${transformOriginXClamped}% top`,
      });
    }
  }, [isOpen, anchorRef]);

  if (!mounted || !isOpen || !coords) return null;

  return createPortal(
    <div
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        transform: "translateX(-50%)",
        zIndex: 9999,
        paddingTop: 4,
        minWidth: DROPDOWN_MIN_WIDTH,
      }}
    >
      {children}
    </div>,
    document.body,
  );
};


interface TabItemProps {
  tab: ResolvedTab;
  index: number;
  isOpen: boolean;
  isTouchDevice: boolean;
  withTopPadding: boolean;
  activeIndicator: "top" | "bottom";
  tabRef: (el: HTMLDivElement | null) => void;
  activeTabRef?: React.MutableRefObject<HTMLAnchorElement | null>;
  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  onDropdownMouseEnter: () => void;
  onDropdownMouseLeave: () => void;
  onToggle: (index: number) => void;
}

const TabItem = memo(({
  tab,
  index,
  isOpen,
  isTouchDevice,
  withTopPadding,
  activeIndicator,
  tabRef,
  activeTabRef,
  onMouseEnter,
  onMouseLeave,
  onDropdownMouseEnter,
  onDropdownMouseLeave,
  onToggle,
}: TabItemProps) => {
  const hasSubtabs = tab.subtabs.length > 0;
  const tabDivRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback((el: HTMLDivElement | null) => {
    tabDivRef.current = el;
    tabRef(el);
  }, [tabRef]);

  if (!hasSubtabs) {
    return (
      <li>
      <Link
        ref={(el) => {
          if (tab.isActive && el && activeTabRef) {
            activeTabRef.current = el;
          }
        }}
        href={tab.href}
        target={tab.target ? tab.target : "_self"}
        className={tabLinkClass(tab.isActive, withTopPadding, activeIndicator)}
      >
        {tab.title}
      </Link>
      </li>
    );
  }

  return (
    <li>
    <div
      ref={setRef}
      className="relative flex items-center h-full"
      onMouseEnter={!isTouchDevice ? () => onMouseEnter(index) : undefined}
      onMouseLeave={!isTouchDevice ? onMouseLeave : undefined}
    >
      <Link
        ref={(el) => {
          if (tab.isActive && el && activeTabRef) {
            activeTabRef.current = el;
          }
        }}
        href={isTouchDevice ? "" : tab.href}
        target={!isTouchDevice && tab.target ? tab.target : "_self"}
        className={tabLinkClass(tab.isActive, withTopPadding, activeIndicator)}
        onClick={
          isTouchDevice
            ? (e) => {
                e.preventDefault();
                onToggle(index);
              }
            : undefined
        }
      >
        <span className="flex items-center gap-1 ">
          {tab.title}
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </span>
      </Link>

      <DropdownPortal anchorRef={tabDivRef as React.RefObject<HTMLDivElement>} isOpen={isOpen}>
        <div
          className="min-w-[160px] rounded-md border border-[var(--stroke-gray-light)] bg-white shadow-lg"
          onMouseEnter={onDropdownMouseEnter}
          onMouseLeave={onDropdownMouseLeave}
        >
          <div className="py-1">
            {tab.subtabs.map((subtab, subIdx) => (
              <Link
                key={`${subtab.title}-${subIdx}`}
                href={subtab.href}
                target={subtab.target}
                className={[
                  "block px-4 py-2 text-sm whitespace-nowrap hover:bg-gray-50 ",
                  subtab.isActive
                    ? "font-[var(--font-bold)] text-[var(--color-primary-950)]"
                    : "text-[var(--text-base-primary)] hover:text-[var(--color-primary-950)]",
                ].join(" ")}
              >
                {subtab.title}
              </Link>
            ))}
          </div>
        </div>
      </DropdownPortal>
    </div>
    </li>
  );
});
TabItem.displayName = "TabItem";

const CustomTabsNav = ({
  customTabsData,
  hotelSlug,
  withTopPadding = false,
  navRef,
  activeTabRef,
  navClassName = "xl-hide-scrollbar mx-4 min-w-0 flex-1 overflow-x-auto  whitespace-nowrap",
  innerClassName = "ml-auto flex w-max gap-2",
  isAttractionTemplate = false,
  activeIndicator = "bottom",
}: CustomTabsNavProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [openTabIndex, setOpenTabIndex] = useState<number | null>(null);
  const [currentHash, setCurrentHash] = useState("");
  const [isTouchDevice, setIsTouchDevice] = useState(false); // + added
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);


  useEffect(() => {
    setCurrentHash(window.location.hash);
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);


  useEffect(() => {
    setIsTouchDevice(window.matchMedia("(hover: none)").matches);
  }, []);

  useEffect(() => {
    if (!isTouchDevice) return;
    const handleClickOutside = (e: MouseEvent) => {
      const clickedInsideATab = tabRefs.current.some(
        (ref) => ref && ref.contains(e.target as Node),
      );
      if (!clickedInsideATab) setOpenTabIndex(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isTouchDevice]);

  useEffect(() => {
    if (!isTouchDevice) return;
    const handleScroll = () => setOpenTabIndex(null);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isTouchDevice]);

  const filteredParams = new URLSearchParams(searchParams?.toString() || "");
  filteredParams.delete("attractionId");
  const queryString = filteredParams.toString()
    ? `?${filteredParams.toString()}`
    : "";


  const resolveTabHref = (tabItem: {
    use_existing_tab?: Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab | null;
    use_custom_link?: boolean | null;
    custom_link?: unknown;
  }): { href: string; target: "_blank" | "_self" | "_parent" | "_top" } => {
    if (tabItem.use_custom_link && tabItem.custom_link) {
      const resolved = generateLinkFromGeneralLink(
        tabItem.custom_link as LinkConfig,
        locale,
      );
      const resolvedTarget=generateTargetFromLink(tabItem.custom_link as LinkConfig)
      return { href: resolved, target: resolvedTarget };
    }
    if (tabItem.use_existing_tab) {
      if (tabItem.use_existing_tab === "attractions") {
        const href = isAttractionTemplate
          ? `/${locale}/attraction-${hotelSlug}${queryString}`
          : `/${locale}/location-${hotelSlug}${queryString}#attractions`;
        return { href, target: "_self" };
      }
      const prefix = EXISTING_TAB_URL_MAP[tabItem.use_existing_tab] ?? "";
      return {
        href: `/${locale}/${prefix}${hotelSlug}`,
        target: "_self",
      };
    }
    return { href: "#", target: "_self" };
  };


 const isTabActive = (href: string): boolean => {
  if (!href || href === "#") return false;

  const rawPath = href.startsWith(`/${locale}/`)
    ? href.slice(`/${locale}/`.length)
    : href;

  let cleanPathname = pathname.startsWith(`/${locale}/`)
    ? pathname.slice(`/${locale}/`.length)
    : pathname;
  cleanPathname = cleanPathname.replace(/^\/+/, "").replace(/\/+$/, "");

  const pathPart = rawPath.split("?")[0].split("#")[0]
    .replace(/^\/+/, "")
    .replace(/\/+$/, "");

  const hrefHash = rawPath.includes("#") ? `#${rawPath.split("#")[1]}` : "";

  if (!pathPart) return false;

  const currentPath = cleanPathname.split("?")[0].split("#")[0].replace(/\/+$/, "");

  const pathMatches =
    currentPath === pathPart ||
    currentPath.startsWith(pathPart + "/");

  if (!pathMatches) return false;

  if (hrefHash) return currentHash === hrefHash;

  // Don't activate the access tab when on the attractions hash
  if (currentHash === "#attractions" && pathPart.startsWith("location-")) return false;

  return true;
};


  const resolvedTabs: ResolvedTab[] = useMemo(()=>{
    return (customTabsData || [])
    .map((tab) => {
    const { href, target } = resolveTabHref(tab.tab_data);
    const title =
      tab.tab_data?.label || tab.item_name || tab.display_name || "";
    const subtabs = (tab.subtabs || [])
      .filter(Boolean)
      .map((subtab) => {
        const { href: subHref, target: subtarget } =
          resolveTabHref(subtab!);
        return {
          title: subtab?.label || "",
          href: subHref,
          isActive: isTabActive(subHref),
          target: subtarget,
        };
      });
    const tabActive = isTabActive(href) || subtabs.some((s) => s.isActive);
    return { title, href, isActive: tabActive, target, subtabs };
  });
  },[customTabsData,locale,pathname,currentHash]) 

  // ── Shared tab link class ─────────────────────────────────────────────────

  


  const handleMouseEnter = useCallback((index: number) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenTabIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenTabIndex(null), 120);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenTabIndex(null), 120);
  }, []);

  const handleToggle = useCallback((index: number) => {
    setOpenTabIndex((prev) => (prev === index ? null : index));
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current); // missing cleanup
    };
  }, []);


  return (
    <nav ref={navRef} className={navClassName}>
      <ul className={innerClassName}>
        {resolvedTabs.map((tab, index) => (
          <TabItem
            key={tab.title}
            tab={tab}
            index={index}
            isOpen={openTabIndex === index}
            isTouchDevice={isTouchDevice}
            withTopPadding={withTopPadding}
            activeIndicator={activeIndicator}
            tabRef={(el) => { tabRefs.current[index] = el; }}
            activeTabRef={activeTabRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDropdownMouseEnter={handleDropdownMouseEnter}
            onDropdownMouseLeave={handleDropdownMouseLeave}
            onToggle={handleToggle}
          />
        ))}
      </ul>
    </nav>
  );
};

export default CustomTabsNav;
